import { yupResolver } from '@hookform/resolvers/yup';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import AppTextField, { InputType } from '../form-inputs/AppTextField';
import Close from '@mui/icons-material/Close';
import { OrgUserUpdatePhone } from '@/api/DTO/user/user.interface';
import { closeSnackbar, useSnackbar } from 'notistack';
import {
	useEditFreeUserPhoneMutation,
	useEditOrgUserPhoneMutation,
} from '@/api/user/userApiSlice';
import { UserCategory } from '@/api/enum/userCategory.enum';
import { UserCatContext } from '@/context/userCategoryContext';
import CloseIcon from '@mui/icons-material/Close';

interface EditPhoneNumberModalProps {
	userId: string;
	phone: string;
}

const EditPhoneNumberModal = (props: EditPhoneNumberModalProps) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const { userCat } = useContext(UserCatContext);

	const { userId, phone } = props;

	const [editOrgPhone] = useEditOrgUserPhoneMutation();
	const [editFreePhone] = useEditFreeUserPhoneMutation();

	const defaultValues: DefaultValues<OrgUserUpdatePhone> = {
		phone: phone,
	};

	const formSchema = Yup.object().shape({
		phone: Yup.string()
			.required(t('errorMessage.required'))
			.test(
				'phone',
				() => t('errorMessage.phone'),
				(phone) => phone?.length == 8 || phone?.length == 0
			),
	});

	const methods = useForm<OrgUserUpdatePhone>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		methods.setValue('userId', userId);
	}, [userId]);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit: SubmitHandler<OrgUserUpdatePhone> = async (data) => {
		if (userCat === UserCategory.ORG) {
			const response = await editOrgPhone(data);
			if ('data' in response) {
				enqueueSnackbar(t('snackbar.editPhoneNumSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
				handleClose();
			}
		} else {
			const response = await editFreePhone(data);
			if ('data' in response) {
				enqueueSnackbar(t('snackbar.editPhoneNumSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
				handleClose();
			}
		}
	};

	return (
		<>
			<IconButton color='primary' onClick={() => setOpen(true)}>
				<CreateOutlinedIcon sx={{ cursor: 'pointer' }} />
			</IconButton>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('common.editPhoneNumber')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='phone'
										label={t('common.phoneNumber')}
										inputType={InputType.number}
										maxLength={8}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default EditPhoneNumberModal;
